import { useEffect, useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Stack,
  Paper,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMe } from '../api/hooks.js';
import { useActiveProject } from '../lib/activeProject.js';
import { api } from '../api/client.js';
import { Heatmap, type HeatPoint } from '../components/Heatmap.js';
import { MetricCard } from '../components/MetricCard.js';
import { useT } from '../i18n/useT.js';

interface ScrollMap {
  maxDepthPx: number;
  sessions: number;
  bands: { depthPx: number; reachedPct: number }[];
}
interface RegionSession {
  id: string;
  projectId: string;
  user: string;
  startedAt: string;
  durationMs: number;
  clicks: number;
}

const fmtDur = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

/** Org-level Click Heatmap & Scroll Map — auto-loads on URL selection, with
 * summary stats and click-through from any hot region to the sessions behind it. */
export function HeatmapPage() {
  const t = useT().analytics;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { projectId: activeProject } = useActiveProject(orgId);

  const [pattern, setPattern] = useState(''); // committed route pattern / raw URL that drives the queries
  // The text in the box, controlled. A freeSolo Autocomplete keeps its own
  // input state, so clearing `pattern` alone left the previous project's page
  // group sitting there — a filter for a page the new project does not have.
  const [input, setInput] = useState('');
  const [grouped, setGrouped] = useState(true); // group same-template pages (default) vs. exact raw URLs
  const [showBg, setShowBg] = useState(true);
  const [region, setRegion] = useState<{ x: number; y: number } | null>(null);

  // Reset the selection when the project or the grouping mode changes.
  useEffect(() => {
    setPattern('');
    setInput('');
    setRegion(null);
  }, [activeProject, grouped]);

  const base = `/v1/orgs/${orgId}/projects/${activeProject}/analytics`;
  const g = grouped ? 'true' : 'false';
  const on = !!orgId && !!activeProject && !!pattern;

  const urls = useQuery({
    enabled: !!orgId && !!activeProject,
    queryKey: ['urls', orgId, activeProject, grouped],
    queryFn: async () => (await api.get(`${base}/urls?grouped=${g}`)).data.data as { url: string; sessions: number }[],
  });

  const heat = useQuery({
    enabled: on,
    queryKey: ['heat', activeProject, pattern, grouped],
    queryFn: async () =>
      (await api.get(`${base}/heatmap?pattern=${encodeURIComponent(pattern)}&grouped=${g}`)).data.data as HeatPoint[],
  });
  const scroll = useQuery({
    enabled: on,
    queryKey: ['scroll', activeProject, pattern, grouped],
    queryFn: async () =>
      (await api.get(`${base}/scrollmap?pattern=${encodeURIComponent(pattern)}&grouped=${g}`)).data as ScrollMap,
  });
  const bg = useQuery({
    enabled: on && showBg,
    queryKey: ['bg', activeProject, pattern, grouped],
    queryFn: async () => {
      const sid = (await api.get(`${base}/sample-session?pattern=${encodeURIComponent(pattern)}&grouped=${g}`)).data
        .sessionId as string | null;
      if (!sid) return null;
      return (await api.get(`/v1/orgs/${orgId}/projects/${activeProject}/sessions/${sid}/events`)).data.events as unknown[];
    },
  });

  const regionQ = useQuery({
    enabled: on && !!region,
    queryKey: ['region', activeProject, pattern, grouped, region?.x, region?.y],
    queryFn: async () =>
      (await api.post(`${base}/heatmap-region`, { pattern, x: region!.x, y: region!.y, grouped })).data
        .data as RegionSession[],
  });

  const points = heat.data ?? [];
  const totalClicks = points.reduce((n, p) => n + p.weight, 0);
  const hottest = points.reduce((m, p) => Math.max(m, p.weight), 0);

  // Size the heatmap stage to the page it was captured on so the backdrop isn't
  // cropped and no click points fall off the canvas. Prefer the recorded
  // viewport (rrweb Meta = type 4); otherwise cover all points, with a floor.
  const meta = (bg.data as { type?: number; data?: { width?: number; height?: number } }[] | undefined)?.find(
    (e) => e?.type === 4,
  );
  const maxX = points.reduce((m, p) => Math.max(m, p.x), 0);
  const maxY = points.reduce((m, p) => Math.max(m, p.y), 0);
  const stageW = Math.max(meta?.data?.width ?? 0, maxX + 60, 1024);
  const stageH = Math.max(meta?.data?.height ?? 0, maxY + 60, 640);
  const reachedMid = scroll.data?.bands.length
    ? Math.round((scroll.data.bands[Math.floor(scroll.data.bands.length / 2)]?.reachedPct ?? 0) * 100)
    : 0;
  const loading = heat.isLoading || scroll.isLoading;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Click Heatmap &amp; Scroll Map</Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          freeSolo
          options={urls.data ?? []}
          loading={urls.isLoading}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.url)}
          value={pattern || null}
          inputValue={input}
          onInputChange={(_, v) => setInput(v)}
          onChange={(_, v) => {
            setRegion(null);
            setPattern(typeof v === 'string' ? v : (v?.url ?? ''));
          }}
          isOptionEqualToValue={(o, v) => (typeof o === 'string' ? o : o.url) === (typeof v === 'string' ? v : v?.url)}
          renderOption={(props, o) => (
            <li {...props} key={o.url}>
              <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace', fontSize: 13 }}>
                {o.url}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {o.sessions} session{o.sessions === 1 ? '' : 's'}
              </Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                urls.data && urls.data.length === 0
                  ? t.heatmap.noPages
                  : grouped
                    ? t.heatmap.selectGroup
                    : t.heatmap.selectUrl
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && on ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <FormControlLabel
          control={<Switch size="small" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />}
          label={t.heatmap.groupSimilar}
          title={t.heatmap.groupSimilarHint}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showBg} onChange={(e) => setShowBg(e.target.checked)} />}
          label={t.heatmap.pageBackground}
        />
      </Stack>

      {!pattern ? (
        <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            {t.heatmap.emptyBody(grouped ? t.heatmap.emptyGroup : t.heatmap.emptyPage)}
            {grouped ? t.heatmap.emptyGrouped : null}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Summary stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
            <Stat label={t.heatmap.sampledSessions} value={scroll.data ? String(scroll.data.sessions) : '—'} />
            <Stat label={t.heatmap.totalClicks} value={totalClicks ? totalClicks.toLocaleString() : '—'} />
            <Stat label={t.heatmap.hottestZone} value={hottest ? t.heatmap.clicksSuffix(hottest) : '—'} />
            <Stat label={t.heatmap.maxScroll} value={scroll.data?.maxDepthPx ? `${scroll.data.maxDepthPx.toLocaleString()} px` : '—'} />
            <Stat label={t.heatmap.reachedMid} value={scroll.data ? `${reachedMid}%` : '—'} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
            <Paper sx={{ p: 2, flex: '1 1 640px', minWidth: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t.heatmap.density}
                </Typography>
                <HeatLegend />
              </Stack>
              {loading ? (
                <Box sx={{ height: 300, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
              ) : (
                <Heatmap
                  points={points}
                  width={stageW}
                  height={stageH}
                  backgroundEvents={showBg ? bg.data ?? null : null}
                  onRegionClick={(x, y) => setRegion({ x, y })}
                />
              )}
            </Paper>

            {scroll.data && scroll.data.bands.length > 0 && (
              <Paper sx={{ p: 2, width: 180 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t.heatmap.scrollDepth}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: 480 }}>
                  {scroll.data.bands.map((b, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1,
                        fontSize: 11,
                        color: b.reachedPct > 0.5 ? '#0b0e14' : '#e5e7eb',
                        bgcolor: `hsl(${Math.round(b.reachedPct * 120)}, 70%, 50%)`,
                      }}
                    >
                      <span>{b.depthPx}px</span>
                      <span>{Math.round(b.reachedPct * 100)}%</span>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        </>
      )}

      {/* Region drill-down popup */}
      <Dialog open={!!region} onClose={() => setRegion(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" component="span">{t.heatmap.regionTitle}</Typography>
            {!regionQ.isLoading && <Chip size="small" label={`${regionQ.data?.length ?? 0}`} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t.heatmap.regionAround(region?.x ?? 0, region?.y ?? 0)}
          </Typography>
          <IconButton onClick={() => setRegion(null)} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: 'auto' }}>
          {regionQ.isLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={26} /></Box>
          ) : (regionQ.data?.length ?? 0) === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {t.heatmap.regionEmpty}
            </Typography>
          ) : (
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t.heatmap.columns.user}</TableCell>
                  <TableCell align="right">{t.heatmap.columns.clicksHere}</TableCell>
                  <TableCell align="right">{t.heatmap.columns.duration}</TableCell>
                  <TableCell>{t.heatmap.columns.started}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(regionQ.data ?? []).map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/projects/${s.projectId}/sessions/${s.id}`} onClick={() => setRegion(null)} underline="hover">
                        {s.user}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{s.clicks}</TableCell>
                    <TableCell align="right">{fmtDur(s.durationMs)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(s.startedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <MetricCard label={label} value={value} />;
}

/** Blue → green → yellow → red heat scale legend. */
function HeatLegend() {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">less</Typography>
      <Box sx={{ width: 90, height: 8, borderRadius: 4, background: 'linear-gradient(90deg,#1e3cc8,#28c878,#f0dc28,#e61e28)' }} />
      <Typography variant="caption" color="text.secondary">more</Typography>
    </Stack>
  );
}
